import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

/**
 * Session-scoped in-memory cache for get_agency_users_directory().
 * Display-only: id, full_name, avatar_url, role, is_active — no email/tokens/assignments.
 */

let cacheUsers = null; // null = not loaded
let cacheById = null; // Map | null
let inflight = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  });
}

function indexUsers(users) {
  const byId = new Map();
  for (const u of users || []) {
    if (u?.id) byId.set(u.id, u);
  }
  return byId;
}

function setCache(users) {
  cacheUsers = Array.isArray(users) ? users : [];
  cacheById = indexUsers(cacheUsers);
  notify();
}

/** Drop cache so the next load/fetch hits the RPC again. */
export function invalidateUsersDirectory() {
  cacheUsers = null;
  cacheById = null;
  inflight = null;
  notify();
}

/**
 * Fetch (or return cached) agency directory rows.
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<Array<{id: string, full_name: string|null, avatar_url: string|null, role: string|null, is_active: boolean}>>}
 */
export async function loadUsersDirectory({ force = false } = {}) {
  if (!force && cacheUsers) return cacheUsers;
  if (!force && inflight) return inflight;

  const pending = api.usersDirectory
    .list()
    .then((rows) => {
      const normalized = (rows || []).map((u) => ({
        id: u.id,
        full_name: u.full_name ?? null,
        avatar_url: u.avatar_url ?? null,
        /** Alias for call sites that still expect headshot_url */
        headshot_url: u.avatar_url ?? null,
        role: u.role ?? null,
        is_active: u.is_active !== false,
      }));
      setCache(normalized);
      return cacheUsers;
    })
    .finally(() => {
      if (inflight === pending) inflight = null;
    });

  inflight = pending;
  return pending;
}

export function refreshUsersDirectory() {
  return loadUsersDirectory({ force: true });
}

/**
 * Sync lookup against the in-memory cache (null if not loaded or unknown id).
 * @param {string|null|undefined} id
 * @returns {{ full_name: string|null, avatar_url: string|null, role: string|null, is_active: boolean } | null}
 */
export function getUserDisplay(id) {
  if (!id || !cacheById) return null;
  const u = cacheById.get(id);
  if (!u) return null;
  return {
    full_name: u.full_name ?? null,
    avatar_url: u.avatar_url ?? null,
    role: u.role ?? null,
    is_active: u.is_active !== false,
  };
}

/** Prefer directory name, then FK join, then denormalized fallback string. */
export function resolveDisplayName(userId, joinUser, fallbackName) {
  const fromDir = getUserDisplay(userId)?.full_name?.trim();
  if (fromDir) return fromDir;
  const fromJoin = joinUser?.full_name?.trim() || joinUser?.email?.trim();
  if (fromJoin) return fromJoin;
  const fb = typeof fallbackName === 'string' ? fallbackName.trim() : '';
  return fb || null;
}

export function resolveAvatarUrl(userId, joinUser) {
  const fromDir = getUserDisplay(userId)?.avatar_url;
  if (fromDir) return fromDir;
  return joinUser?.avatar_url || joinUser?.headshot_url || null;
}

/** Active directory users sorted by name (for pickers / filters). */
export function getActiveDirectoryUsers(users = cacheUsers) {
  const list = (users || []).filter((u) => u.is_active !== false);
  list.sort((a, b) => {
    const aName = (a.full_name || '').toLowerCase();
    const bName = (b.full_name || '').toLowerCase();
    return aName.localeCompare(bName);
  });
  return list;
}

/**
 * React hook: loads directory once per session, shares module cache.
 * @returns {{
 *   users: Array,
 *   byId: Map,
 *   loading: boolean,
 *   getUserDisplay: (id: string) => object|null,
 *   resolveDisplayName: typeof resolveDisplayName,
 *   resolveAvatarUrl: typeof resolveAvatarUrl,
 *   refresh: () => Promise<Array>,
 *   invalidate: () => void,
 * }}
 */
export function useUsersDirectory() {
  const [users, setUsers] = useState(() => cacheUsers || []);
  const [loading, setLoading] = useState(() => cacheUsers == null);

  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (!cancelled) {
        setUsers(cacheUsers || []);
        setLoading(cacheUsers == null && !!inflight);
      }
    };
    listeners.add(sync);

    if (cacheUsers) {
      setUsers(cacheUsers);
      setLoading(false);
    } else {
      setLoading(true);
      loadUsersDirectory()
        .then((rows) => {
          if (!cancelled) {
            setUsers(rows);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setUsers([]);
            setLoading(false);
          }
        });
    }

    return () => {
      cancelled = true;
      listeners.delete(sync);
    };
  }, []);

  const refresh = useCallback(() => refreshUsersDirectory(), []);
  const invalidate = useCallback(() => {
    invalidateUsersDirectory();
  }, []);

  const byId = cacheById || indexUsers(users);

  return {
    users,
    byId,
    loading,
    getUserDisplay,
    resolveDisplayName,
    resolveAvatarUrl,
    refresh,
    invalidate,
  };
}
